import { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, Sparkles, Compass,
  ChevronLeft, ChevronRight, Bitcoin, ArrowUpRight,
  Hand, BadgeCheck, Wallet, PartyPopper, MessageCircleHeart, Pause, Play
} from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useI18n } from "../lib/i18n";
import DestinationCard, { WaitlistModal, FlagBadge, StatusChip, LiveDot } from "../components/DestinationCard";
import { GooglePlayBadge, AppStoreBadge } from "../components/AppBadges";
import HeroMarquee from "../components/HeroMarquee";

const HERO_BG = "/brand/hero-bg.png";
const SECONDARY_IMG = "https://images.unsplash.com/photo-1627761801957-4bf6cfb4fa20?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

// Editorial taglines per destination (shown on hero deck)
const TAGLINES = {
  "ibiza-spain":          "Nightlife & Villas",
  "custom-trip":          "Designed for you",
  "mykonos-greece":       "Aegean Nightlife",
  "dubai-uae":            "Skyline & Desert",
  "miami-usa":            "Beach & Art Deco",
  "tulum-mexico":         "Jungle & Cenotes",
  "bali-indonesia":       "Cliff-top Villas",
  "marbella-spain":       "Mediterranean Glamour",
  "monaco-monaco":        "Harbor & Heritage",
  "saint-tropez-france":  "Côte d'Azur",
  "cannes-france":        "La Croisette",
  "courchevel-france":    "Alpine Luxury",
};

const AUTO_INTERVAL_MS = 5000;

/** Stacked-deck destination card. tilt = -1 (left), 0 (front), 1 (right). */
function HeroCard({ d, tilt, onExplore, onWaitlist, isActive }) {
  const { t } = useI18n();
  if (!d) return null;
  const isBookable = d.status === "active";
  const tagline = TAGLINES[d.slug] || d.hero_subtitle || "";

  // Visual offsets per tilt
  const offsetClass = tilt === 0
    ? "z-30 scale-100 rotate-0 translate-x-0 opacity-100"
    : tilt === 1
    ? "z-20 scale-[0.9] rotate-[6deg] translate-x-[12%] opacity-50"
    : "z-20 scale-[0.9] -rotate-[6deg] -translate-x-[12%] opacity-50";

  return (
    <article
      className={cx(
        "absolute inset-0 mx-auto rounded-[28px] overflow-hidden",
        "transition-all duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
        "border border-white/10 shadow-ink",
        offsetClass
      )}
      style={{ width: "min(100%, 360px)" }}
      data-testid={isActive ? "hero-card-active" : `hero-card-${d.slug}`}
      aria-hidden={!isActive}
    >
      {d.hero_media_url && (
        <img
          src={d.hero_media_url}
          alt={d.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-ink/10"/>
      {!isBookable && <div className="absolute inset-0 backdrop-blur-[1.5px]"/>}

      <div className="relative h-full flex flex-col justify-between p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="chip">
            <FlagBadge code={d.flag_emoji}/>
            <span className="!tracking-widest2">{d.country}</span>
          </span>
          <StatusChip status={d.status}/>
        </div>

        <div>
          <div className="eyebrow !text-[11px] mb-2">{t("home.hero_card_eyebrow", "Featured Destination")}</div>
          <h3 className="section-title text-[34px] md:text-[40px] text-bone leading-[0.98]" data-testid="hero-card-title">
            {d.name}
          </h3>
          <p className="mt-2 text-sm text-bone/85 flex items-center gap-2">
            <FlagBadge code={d.flag_emoji}/>
            {tagline}
          </p>
          <div className="mt-5">
            {isBookable ? (
              <button onClick={onExplore} className="btn btn-primary text-[12px]" data-testid="hero-explore-btn">
                {d.slug === "custom-trip"
                  ? t("home.hero_custom_btn", "Plan a custom trip")
                  : t("home.hero_explore_btn", `Explore ${d.name}`, { name: d.name })}
                <ArrowUpRight size={14}/>
              </button>
            ) : (
              <button onClick={onWaitlist} className="btn btn-outline text-[12px]" data-testid="hero-waitlist-btn">
                {t("home.hero_waitlist_btn", "Join waitlist")}
                <ArrowUpRight size={14}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function HomePage({ onOpenConcierge }) {
  const { t } = useI18n();
  const [destinations, setDestinations] = useState([]);
  const [waitlistFor, setWaitlistFor] = useState(null);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [wordIdx, setWordIdx] = useState(0);

  // Rotating slogan words are pulled from i18n so they translate too
  const SLOGAN_WORDS = useMemo(() => [
    t("home.slogan_word_1", "find and book"),
    t("home.slogan_word_2", "discover"),
    t("home.slogan_word_3", "book with crypto"),
    t("home.slogan_word_4", "enjoy"),
  ], [t]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/destinations");
        setDestinations(data.items);
      } catch (e) { setError(formatErr(e)); }
    })();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      document.querySelectorAll(".reveal").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight - 80) el.classList.add("in");
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const active = destinations.filter((d) => d.status === "active");
  const coming = destinations.filter((d) => d.status === "comingSoon");
  const ordered = useMemo(() => [...active, ...coming], [destinations]);

  // Auto-advance carousel
  useEffect(() => {
    if (paused || ordered.length === 0) return;
    const id = setInterval(() => setActiveIndex((i) => (i + 1) % ordered.length), AUTO_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused, ordered.length]);

  // Rotating slogan word
  useEffect(() => {
    const id = setInterval(() => setWordIdx((i) => (i + 1) % SLOGAN_WORDS.length), 2800);
    return () => clearInterval(id);
  }, []);

  const activeDest = ordered[activeIndex] || null;
  const nextDest = ordered[(activeIndex + 1) % Math.max(ordered.length, 1)] || null;
  const prevDest = ordered[(activeIndex - 1 + ordered.length) % Math.max(ordered.length, 1)] || null;

  const next = () => { setPaused(true); setActiveIndex((i) => (i + 1) % Math.max(ordered.length, 1)); };
  const prev = () => { setPaused(true); setActiveIndex((i) => (i - 1 + ordered.length) % Math.max(ordered.length, 1)); };
  const goto = (i) => { setPaused(true); setActiveIndex(i); };

  // Touch / pointer swipe for the deck
  const swipeRef = useRef({ startX: 0, active: false });
  const onDeckPointerDown = (e) => {
    swipeRef.current = { startX: e.clientX, active: true };
    setPaused(true);
  };
  const onDeckPointerUp = (e) => {
    if (!swipeRef.current.active) return;
    const dx = e.clientX - swipeRef.current.startX;
    swipeRef.current.active = false;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next(); else prev();
    }
  };

  const exploreActive = () => {
    if (!activeDest) return;
    if (activeDest.slug === "custom-trip") window.location.href = "/custom-trip";
    else window.location.href = `/destinations/${activeDest.slug}`;
  };

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-[100svh] overflow-hidden">
        {/* Earth image as background */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-deep/80 via-ink/55 to-ink"/>
          <div className="absolute inset-0 aurora opacity-50"/>
          <div className="grain absolute inset-0"/>
        </div>

        <div className="relative pt-28 md:pt-32 pb-20 md:pb-24 px-5 md:px-10 mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-10 items-center min-h-[78svh]">
            {/* LEFT — slogan + app badges (mobile-centred, lg-left) */}
            <div className="lg:col-span-7 reveal text-center lg:text-left">
              <div className="eyebrow mb-5 inline-flex items-center gap-3">
                <span className="h-px w-10 bg-teal"/> {t("common.brand_tagline", "Crypto-native travel · Powered by Meet & Mint")}
              </div>

              {/* 3-line slogan — static on lines 1 & 3, rotating word on line 2 (one line, no wrap) */}
              <h1 className="section-title text-bone drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
                <span className="block text-[28px] sm:text-4xl lg:text-[44px] leading-[1.1] text-bone/95">
                  {t("home.slogan_line_1", "The best way to")}
                </span>
                <span
                  className="block my-1 md:my-2 text-[34px] sm:text-[54px] lg:text-[72px] xl:text-[82px] leading-[1] min-h-[1.1em] whitespace-nowrap"
                  data-testid="slogan-rotating-line"
                >
                  <span
                    key={wordIdx}
                    className="font-script text-teal word-flip inline-block"
                    data-testid="rotating-word"
                  >
                    {SLOGAN_WORDS[wordIdx]}
                  </span>
                </span>
                <span className="block text-[28px] sm:text-4xl lg:text-[44px] leading-[1.1] text-bone/95">
                  {t("home.slogan_line_3", "premium services.")}
                </span>
              </h1>

              <p className="mt-6 max-w-xl mx-auto lg:mx-0 text-base md:text-lg text-bone/80 leading-relaxed drop-shadow-[0_1px_12px_rgba(0,0,0,0.75)]">
                {t("home.subtitle", "Verified villas, yachts, tables and bespoke moments — booked discreetly through one concierge.")}
              </p>

              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-3">
                <GooglePlayBadge />
                <AppStoreBadge />
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-4">
                <a href="#destinations" className="btn btn-ghost text-[12px] !px-0 !py-0" data-testid="btn-hero-explore">
                  <Compass size={14}/> {t("home.explore_destinations", "Explore destinations")}
                </a>
                <span className="text-muted/40">·</span>
                <button onClick={onOpenConcierge} className="btn btn-ghost text-[12px] !px-0 !py-0" data-testid="btn-hero-concierge">
                  <Sparkles size={14}/> {t("home.talk_to_concierge", "Talk to concierge")}
                </button>
              </div>
            </div>

            {/* RIGHT — Stacked deck of destination cards (swipeable) */}
            <div
              className="lg:col-span-5 relative"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div
                className="relative w-full max-w-[400px] aspect-[3/4] mx-auto touch-pan-y select-none"
                onPointerDown={onDeckPointerDown}
                onPointerUp={onDeckPointerUp}
                onPointerCancel={() => { swipeRef.current.active = false; }}
                data-testid="hero-deck"
              >
                {prevDest && prevDest !== activeDest && (
                  <HeroCard d={prevDest} tilt={-1} isActive={false}/>
                )}
                {nextDest && nextDest !== activeDest && (
                  <HeroCard d={nextDest} tilt={1} isActive={false}/>
                )}
                {activeDest && (
                  <HeroCard
                    d={activeDest}
                    tilt={0}
                    isActive
                    onExplore={exploreActive}
                    onWaitlist={() => { setPaused(true); setWaitlistFor(activeDest); }}
                  />
                )}

                {/* Side arrows — sit OUTSIDE the card on sm+ screens, just inside on mobile */}
                <button
                  onClick={prev}
                  aria-label="Previous destination"
                  className="absolute top-1/2 -translate-y-1/2 left-1 sm:-left-6 md:-left-10 lg:-left-14 z-40 h-11 w-11 grid place-items-center rounded-full glass-solid border border-white/10 hover:text-teal hover:border-teal/50 transition shadow-ink"
                  data-testid="hero-prev"
                >
                  <ChevronLeft size={20}/>
                </button>
                <button
                  onClick={next}
                  aria-label="Next destination"
                  className="absolute top-1/2 -translate-y-1/2 right-1 sm:-right-6 md:-right-10 lg:-right-14 z-40 h-11 w-11 grid place-items-center rounded-full glass-solid border border-white/10 hover:text-teal hover:border-teal/50 transition shadow-ink"
                  data-testid="hero-next"
                >
                  <ChevronRight size={20}/>
                </button>
              </div>

              {/* Controls — dots + pause */}
              <div className="relative mt-6 flex items-center justify-center gap-3 max-w-[400px] mx-auto">
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                  {ordered.map((d, i) => (
                    <button
                      key={d.id}
                      onClick={() => goto(i)}
                      className={`h-1.5 rounded-full transition-all ${i === activeIndex ? "w-6 bg-teal" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                      aria-label={d.name}
                      data-testid={`hero-dot-${d.slug}`}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setPaused((p) => !p)}
                  aria-label={paused ? "Play" : "Pause"}
                  className="ml-2 h-9 w-9 grid place-items-center rounded-full glass-solid hover:text-teal transition"
                  data-testid="hero-pause"
                >
                  {paused ? <Play size={13}/> : <Pause size={13}/>}
                </button>
              </div>

              <div className="relative mt-3 text-center text-[11px] font-bitcount tracking-widest2 text-muted uppercase">
                {activeIndex + 1} / {ordered.length || "…"} ·{" "}
                <span className="text-teal">{activeDest?.name || ""}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="absolute bottom-0 inset-x-0">
          <HeroMarquee />
        </div>
      </section>

      {/* DESTINATIONS GRID */}
      <section id="destinations" className="relative px-5 md:px-10 mx-auto max-w-7xl py-20 md:py-24">
        <div className="mb-12 reveal max-w-3xl">
          <div className="eyebrow mb-4">{t("home.atlas_eyebrow", "The Atlas")}</div>
          <h2 className="section-title text-4xl sm:text-6xl">{t("home.atlas_title_line_1", "Twelve destinations.")}<br/>{t("home.atlas_title_line_2", "One concierge.")}</h2>
          <p className="mt-6 text-sm md:text-base text-muted leading-relaxed max-w-2xl">
            {t("home.atlas_copy", "We launch in Ibiza and grow city by city — verifying every villa, yacht and provider before opening the gates.")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {active.map((d, i) => (
            <div key={d.id} className="reveal" style={{ transitionDelay: `${i * 60}ms` }}>
              <DestinationCard d={d} onWaitlist={setWaitlistFor}/>
            </div>
          ))}
          {coming.map((d, i) => (
            <div key={d.id} className="reveal" style={{ transitionDelay: `${(i + active.length) * 50}ms` }}>
              <DestinationCard d={d} onWaitlist={setWaitlistFor}/>
            </div>
          ))}
        </div>

        {error && <div className="text-rose-300 text-sm mt-6" data-testid="atlas-error">{error}</div>}

        <WaitlistModal destination={waitlistFor} onClose={() => setWaitlistFor(null)}/>
      </section>

      {/* HOW IT WORKS — with icons */}
      <section className="relative px-5 md:px-10 mx-auto max-w-7xl py-20 md:py-24 grid md:grid-cols-12 gap-12 reveal">
        <div className="md:col-span-5">
          <div className="relative rounded-3xl overflow-hidden h-[480px]">
            <img src={SECONDARY_IMG} alt="Yacht charter" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent"/>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-script text-2xl text-bone">"It feels like calling a friend with the keys to the island."</div>
              <div className="eyebrow mt-2">— Founding member</div>
            </div>
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="eyebrow mb-3">{t("home.how_eyebrow", "How it works")}</div>
          <h2 className="section-title text-4xl sm:text-5xl mb-10">{t("home.how_title", "Four steps. Discreetly choreographed.")}</h2>
          <div className="space-y-7">
            {[
              { n: "01", t: t("home.how_step_1_title", "Choose or describe."), d: t("home.how_step_1_copy", "Pick a verified service from our Ibiza catalog, or tell the concierge what you have in mind."), i: <Hand size={20}/> },
              { n: "02", t: t("home.how_step_2_title", "We confirm availability."), d: t("home.how_step_2_copy", "Our concierge contacts the provider directly. You're never left waiting on a marketplace."), i: <BadgeCheck size={20}/> },
              { n: "03", t: t("home.how_step_3_title", "Pay your deposit."), d: t("home.how_step_3_copy", "Crypto or card. Funds are held safely as a protected deposit until the service is delivered."), i: <Wallet size={20}/> },
              { n: "04", t: t("home.how_step_4_title", "Enjoy. Then confirm."), d: t("home.how_step_4_copy", "Live the moment. Confirm completion in one tap. Funds release to the provider, your review goes on record."), i: <PartyPopper size={20}/> },
            ].map((s) => (
              <div key={s.n} className="flex gap-5 items-start">
                <div className="h-12 w-12 shrink-0 rounded-2xl border hairline grid place-items-center text-teal bg-white/[0.02]">
                  {s.i}
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-3">
                    <span className="font-script text-teal text-2xl">{s.n}</span>
                    <div className="font-serif-display text-2xl text-bone">{s.t}</div>
                  </div>
                  <div className="text-sm text-muted mt-1 max-w-md">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="relative px-5 md:px-10 mx-auto max-w-7xl py-14 md:py-16 reveal">
        <div className="grid md:grid-cols-4 gap-5">
          {[
            { i: <ShieldCheck size={18}/>, t: t("home.trust_verified_title", "Verified providers"), d: t("home.trust_verified_copy", "Every villa, yacht, club and team is reviewed by a human concierge before listing.") },
            { i: <Lock size={18}/>, t: t("home.trust_deposits_title", "Protected deposits"), d: t("home.trust_deposits_copy", "Funds are reserved on the platform and released only after service confirmation.") },
            { i: <Bitcoin size={18}/>, t: t("home.trust_crypto_title", "Crypto-native"), d: t("home.trust_crypto_copy", "Pay with stablecoins, cards, or your Meet & Mint wallet — whatever suits you.") },
            { i: <MessageCircleHeart size={18}/>, t: t("home.trust_desk_title", "Concierge desk"), d: t("home.trust_desk_copy", "Talk to a real person during business hours, and our AI travel agent — available 24/7.") },
          ].map((c, k) => (
            <div key={k} className="luxe-card p-6">
              <div className="text-teal mb-4">{c.i}</div>
              <div className="font-serif-display text-2xl text-bone">{c.t}</div>
              <div className="text-sm text-muted mt-2 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-5 md:px-10 mx-auto max-w-7xl py-14 md:py-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="luxe-card p-10 reveal">
            <div className="eyebrow mb-3">{t("home.cta_provider_eyebrow", "For providers")}</div>
            <h3 className="section-title text-3xl md:text-4xl mb-4">{t("home.cta_provider_title", "Bookings from crypto-native, lifestyle travellers.")}</h3>
            <p className="text-sm text-muted max-w-md">{t("home.cta_provider_copy", "We bring verified clients, manage deposits, and you focus on what you do best. Simple commission, no monthly fees.")}</p>
            <Link to="/become-provider" className="btn btn-primary mt-6" data-testid="cta-become-provider">{t("home.cta_provider_btn", "Apply to provide")} →</Link>
          </div>
          <div className="luxe-card p-10 reveal">
            <div className="eyebrow mb-3">{t("home.cta_affiliate_eyebrow", "For creators & affiliates")}</div>
            <h3 className="section-title text-3xl md:text-4xl mb-4">{t("home.cta_affiliate_title", "Earn by promoting premium experiences.")}</h3>
            <p className="text-sm text-muted max-w-md">{t("home.cta_affiliate_copy", "Crypto-paid commissions on confirmed bookings. Our affiliate program opens with our public launch — join the early list.")}</p>
            <Link to="/become-affiliate" className="btn btn-outline mt-6" data-testid="cta-become-affiliate">{t("home.cta_affiliate_btn", "Become an affiliate")} →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
